import { EntityBinding } from "@shared/mirage/metadata/BaseEntityRecordBinding";
import DateFormatter from "@shared/mirage/helpers/DateFormatter";

export class SQLFilterConverter {
    private static processRule(rule: any, isTopLevel: boolean, metadata?: EntityBinding<unknown>): string {
        if (!rule) {
            return "";
        }
        if (typeof rule === "string") {
            return rule.toUpperCase();
        }
        if (rule.rules && Array.isArray(rule.rules)) {
            if (rule.rules.length === 1) {
                return this.processRule(rule.rules[0], isTopLevel, metadata);
            } else {
                const inner = this.processTokens(rule.rules, false, metadata);
                return isTopLevel ? `(${inner})` : inner;
            }
        }
        const fieldName = rule.field;
        const field = metadata?.columns ? (metadata.columns as Record<string, { dbColumnName: string }>)[fieldName]?.dbColumnName || fieldName : fieldName;
        const operator = rule.operator;
        let value = rule.value;

        if (operator === "notNull" || operator === "isNotEmpty") {
            return `${field} IS NOT NULL`;
        } else if (operator === "isNull") {
            return `${field} IS NULL`;
        } else {
            const timestamp = Date.parse(value);
            if (typeof value !== "number" && !isNaN(timestamp)) {
                value = `'${DateFormatter.formatToDateTimeAmPm(value)}'`;
            } else if (typeof value === "object" && value !== null) {
                value = value.name ?? value.id;
            }
            if (typeof value === "number" || !isNaN(Number(value))) {
                return `${field} ${operator} ${value}`;
            } else {
                return `${field} ${operator} '${value}'`;
            }
        }
    }

    private static processTokens(tokens: any, isTopLevel: boolean, metadata?: EntityBinding<unknown>): string {
        if (isTopLevel) {
            const lines = [];
            const first = this.processRule(tokens[0], true, metadata);
            lines.push(first);
            for (let i = 1; i < tokens.length; i += 2) {
                if (typeof tokens[i] !== "string") {
                    continue;
                }
                const op = tokens[i].toUpperCase();
                const tokenStr = this.processRule(tokens[i + 1], true, metadata);
                lines.push(`${op} ${tokenStr}`);
            }
            return lines.join("\n");
        } else {
            const groups = [];
            let currentGroup: string[] = [];
            tokens.forEach((token: string) => {
                if (typeof token === "string" && token.toLowerCase() === "and") {
                    groups.push(currentGroup);
                    currentGroup = [];
                } else {
                    currentGroup.push(token);
                }
            });
            if (currentGroup.length > 0) {
                groups.push(currentGroup);
            }

            const formattedGroups = groups.map((group) => {
                const parts = group.map((token) => {
                    if (typeof token === "string") {
                        return token.toUpperCase();
                    } else {
                        return this.processRule(token, false, metadata);
                    }
                });
                const groupStr = parts.join(" ");
                if (group.length > 1 && groupStr.includes("OR")) {
                    return `(${groupStr})`;
                } else {
                    return groupStr;
                }
            });
            return formattedGroups.join(" AND ");
        }
    }

    public static convertQueryToSQL(query: any, metadata?: EntityBinding<unknown>): string {
        if (!query.rules || !Array.isArray(query.rules)) {
            return this.processRule(query, true, metadata);
        }
        return this.processTokens(query.rules, true, metadata);
    }
}
